/**
 * Central HTML sanitiser for the whole app.
 *
 * Every place we call `dangerouslySetInnerHTML` must pass the input
 * through this helper. Uses DOMPurify with a conservative allow-list
 * that fits editorial content (Journal articles) and translated UI
 * strings (which may contain light formatting):
 *   - text-formatting tags (b, i, em, strong, u, br, span…)
 *   - block tags produced by Quill (p, h1..h4, ul, ol, li, blockquote…)
 *   - inline media in articles (img, iframe for YouTube embeds)
 *   - safe anchors with rel/target enforcement
 *
 * Anything else — <script>, <style>, on* handlers, javascript: URIs,
 * data: images beyond safe types — is stripped.
 */
import DOMPurify from "dompurify";

// Force `target=_blank` links to also carry `rel="noopener noreferrer"`
// so third-party links can't touch our window.opener.
if (typeof window !== "undefined") {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

const ARTICLE_CONFIG = {
  ALLOWED_TAGS: [
    "a", "b", "i", "em", "strong", "u", "s", "br", "span", "small",
    "p", "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li", "blockquote", "pre", "code",
    "img", "figure", "figcaption",
    "iframe", "video", "source",
    "hr", "table", "thead", "tbody", "tr", "th", "td",
  ],
  ALLOWED_ATTR: [
    "href", "target", "rel", "title", "alt", "src", "class",
    "width", "height", "loading",
    "allow", "allowfullscreen", "frameborder", "referrerpolicy",
    "controls", "poster",
  ],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

const INLINE_CONFIG = {
  ALLOWED_TAGS: ["b", "i", "em", "strong", "u", "s", "br", "span", "small", "a"],
  ALLOWED_ATTR: ["href", "target", "rel", "title", "class"],
};

/** Sanitise long-form HTML from the Journal editor. */
export function sanitizeArticleHtml(html) {
  if (!html || typeof window === "undefined") return "";
  return DOMPurify.sanitize(String(html), ARTICLE_CONFIG);
}

/** Sanitise short translated UI strings (light inline formatting only). */
export function sanitizeInlineHtml(html) {
  if (!html || typeof window === "undefined") return "";
  return DOMPurify.sanitize(String(html), INLINE_CONFIG);
}
